"use client";

import { Link } from "@softmax/design-system";
import {
  breakpoints,
  colors,
  fonts,
  fontSizes,
  spacing,
} from "@softmax/design-system/tokens.stylex";
import * as stylex from "@stylexjs/stylex";
import { type ReactNode } from "react";
import { coworldLayout } from "./coworld-layout.styles";

export function CoworldFrame({
  footerLinks,
  title,
  description,
  back,
  actions,
  children,
}: Readonly<{
  title: string;
  description?: string;
  back: Readonly<{ href: string; label: string }>;
  footerLinks: readonly Readonly<{ label: string; href: string }>[];
  actions?: ReactNode;
  children: ReactNode;
}>) {
  return (
    <main id="main" tabIndex={-1} {...stylex.props(styles.page)}>
      <header {...stylex.props(styles.header)}>
        <div {...stylex.props(coworldLayout.muted)}>
          <Link href={back.href} focus="native">
            ‹ {back.label}
          </Link>
        </div>
        <div {...stylex.props(styles.titleRow)}>
          <h1 {...stylex.props(styles.title)}>{title}</h1>
          {actions}
        </div>
        {description && (
          <p {...stylex.props(styles.description)}>{description}</p>
        )}
      </header>
      {children}
      <footer {...stylex.props(styles.footer)}>
        <span>Softmax</span>
        {footerLinks.map((link) => (
          <Link key={link.href} href={link.href} focus="native">
            {link.label}
          </Link>
        ))}
      </footer>
    </main>
  );
}
const styles = stylex.create({
  page: {
    maxWidth: 1200,
    marginInline: "auto",
    paddingInline: {
      default: spacing.space32,
      [breakpoints.compact]: spacing.space16,
    },
    paddingTop: spacing.space64,
    paddingBottom: spacing.space24,
    minHeight: "100svh",
    boxSizing: "border-box",
    color: colors.foreground,
    fontFamily: fonts.sans,
    display: "flex",
    flexDirection: "column",
  },
  header: {
    display: "grid",
    gap: spacing.space8,
    marginBottom: spacing.space24,
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: spacing.space12,
  },
  title: {
    overflowWrap: "anywhere",
    margin: 0,
    fontFamily: fonts.mono,
    fontSize: fontSizes.title,
    fontWeight: 600,
    lineHeight: 1.4,
    letterSpacing: -0.5,
    minWidth: 0,
  },
  description: {
    margin: 0,
    color: colors.secondary,
    fontSize: fontSizes.label,
  },
  footer: {
    marginTop: "auto",
    paddingTop: spacing.space48,
    display: "flex",
    gap: spacing.space16,
    fontSize: fontSizes.caption,
    color: colors.secondary,
  },
});
